/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { FormValidationStatus, MultiSelect, Typography2 } from '@coursera/cds-core';

import type { RecommendationItemType, RecommendationType } from 'bundles/enterprise-admin-ai-program-builder/types';

import _t from 'i18n!nls/enterprise-admin-ai-program-builder';

export const getRecommendationItemTypeOptions = (recommendationType: RecommendationType): RecommendationItemType[] => {
  if (recommendationType === 'LEARNING_PATHS') {
    return ['COURSE', 'PROJECT', 'GUIDED_PROJECT'];
  }
  return ['VIDEO', 'COURSE', 'PROJECT', 'GUIDED_PROJECT', 'SPECIALIZATION', 'PROFESSIONAL_CERTIFICATE'];
};

export const ItemTypeMultiSelect = ({
  value,
  onChange,
  recommendationType,
  disabled,
}: {
  value: RecommendationItemType[];
  onChange: (value: RecommendationItemType[]) => void;
  recommendationType: RecommendationType;
  disabled: boolean;
}) => {
  const labels = {
    VIDEO: _t('Video'),
    PROJECT: _t('Project'),
    GUIDED_PROJECT: _t('Guided Project'),
    COURSE: _t('Course'),
    SPECIALIZATION: _t('Specialization'),
    PROFESSIONAL_CERTIFICATE: _t('Professional Certificate'),
  };
  const descriptions = {
    VIDEO: _t('Explore subjects by watching short videos for quick learning.'),
    PROJECT: _t('Learn a new tool or skill in an interactive, hands-on environment.'),
    GUIDED_PROJECT: _t('Build job-relevant skills in under 2 hours with hands-on tutorials.'),
    COURSE: _t('Learn from top instructors with graded assignments, videos, and discussion forums.'),
    SPECIALIZATION: _t('Get in-depth knowledge of a subject by completing a series of courses and projects.'),
    PROFESSIONAL_CERTIFICATE: _t('Earn career credentials from industry leaders that demonstrate your expertise.'),
  };
  const options = getRecommendationItemTypeOptions(recommendationType);
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 8px;
        ${disabled &&
        css`
          pointer-events: none;
        `}
      `}
    >
      <Typography2 variant="subtitleMedium" component="label" id="item-type-multi-select-label">
        {_t('Select what content types to include')}
      </Typography2>
      <MultiSelect
        placeholder={_t('Content Types')}
        aria-labelledby="item-type-multi-select-label"
        value={value}
        onChange={(keys) => {
          onChange(Array.from(keys) as RecommendationItemType[]);
        }}
      >
        {options.map((itemType) => (
          <MultiSelect.Option key={itemType} supportText={descriptions[itemType]}>
            {labels[itemType]}
          </MultiSelect.Option>
        ))}
      </MultiSelect>
      {value.length === 0 && (
        <FormValidationStatus label={_t('Select at least one content type')} validationStatus="error" />
      )}
    </div>
  );
};
